import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";



type Framework = Record<"value" | "label", string>;

interface SortableItemProps {
  framework: Framework;
  onRemove: (framework: Framework) => void;
}

function SortableItem({ framework, onRemove }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: framework.value });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 p-2 bg-white border rounded-md shadow-sm",
        isDragging && "opacity-50"
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </button>
      <span className="flex-1">{framework.label}</span>
      <button
        onClick={() => onRemove(framework)}
        className="text-gray-400 hover:text-gray-600"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface SortableMultiSelectProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> {
  frameworks: Framework[];
  selected: Framework[];
  setSelected: React.Dispatch<React.SetStateAction<Framework[]>>;
}

const SortableMultiSelect = React.forwardRef<HTMLInputElement, SortableMultiSelectProps>(
  ({ frameworks, selected, setSelected, ...props }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSelected((items) => {
        const oldIndex = items.findIndex((item) => item.value === active.id);
        const newIndex = items.findIndex((item) => item.value === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleUnselect = (framework: Framework) => {
    setSelected(selected.filter((s) => s.value !== framework.value));
  };

  const handleSelect = (framework: Framework) => {
    if (!selected.some((s) => s.value === framework.value)) {
      setSelected((prev) => [...prev, framework]);
    }
    setInputValue("");
  };

  const selectables = frameworks.filter(
    (framework) => !selected.some((s) => s.value === framework.value),
  );

  const filteredSelectables = selectables.filter((framework) =>
    framework.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <p className="text-sm text-muted-foreground">Arrastra los elementos para ordenarlos</p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={selected.map((s) => s.value)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {selected.map((framework) => (
              <SortableItem
                key={framework.value}
                framework={framework}
                onRemove={handleUnselect}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="relative">
        <div
          className={cn("flex h-full w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer")}
          onClick={() => setOpen(!open)}
        >
          <input
            ref={ref}
            value={String(inputValue)}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder="Seleccionar categorías..."
            className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
            {...props}
          />
        </div>
        {open && (
          <div className="absolute top-full z-10 w-full mt-1 rounded-md border bg-white text-popover-foreground shadow-md max-h-60 overflow-auto">
            {filteredSelectables.length > 0 ? (
              filteredSelectables.map((framework) => (
                <div
                  key={framework.value}
                  onClick={() => handleSelect(framework)}
                  className="p-2 cursor-pointer hover:bg-gray-100"
                >
                  {framework.label}
                </div>
              ))
            ) : (
              <div className="p-2 text-sm text-muted-foreground">No hay categorías disponibles</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

SortableMultiSelect.displayName = "SortableMultiSelect";

export { SortableMultiSelect };